import _ from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { EquipmentTypeDto, DumpLineItemDto, QuoteDto, DumpRateDto, RegionDto, SiteDto } from '../../../../../dtos';
import { QuoteType, UnitOfMeasure, countries, emptyGuid, unitsOfMeasure } from '../../../../../util';
import { useGetRegionQuery } from '../../../../../store/generated/generatedApi';
import {
    IFormProps,
    IFormFieldValidationConfig,
    isNotBlank,
    runFormValidation,
    runFieldValidation,
    isGreaterThanOrEqualTo,
    isShorterThanMaxLength,
} from '../../../../CoreLib/library';
import { SelectChangeEvent } from '@mui/material';

export interface IDumpLineItemFormValues {
    dumpRateId: string;
    quoteId: string;
    equipmentTypeId: string;
    dumpType?: string;
    cost?: number;
    rate?: number;
    quantity?: number;
    unitOfMeasure: string;
    newHaulingLineItemRate?: number;
    newHaulingLineItemDriverRate?: number;
    newHaulingLineItemBrokerRate?: number;
    lineItemMemo?: string;
    driverMemo?: string;
    isActive: boolean;
    customerJobNumber?: string;
    poNumber?: string;
    isCustomPoNumber: boolean;
    isCustomerJobNumberCustom: boolean;
    haulToAddressLine1?: string;
    haulToAddressLine2?: string;
    haulToCity?: string;
    haulToState?: string;
    haulToZipCode?: string;
    haulToCountry?: string;
    haulToDirections?: string;
    haulToSiteName?: string;
    haulToRegion?: RegionDto;
}

export const DEFAULT_DUMP_LINE_ITEM: DumpLineItemDto = {
    id: emptyGuid,
    siteId: undefined,
    site: undefined,
    equipmentTypeId: emptyGuid,
    dumpRateId: emptyGuid,
    quoteId: '',
    cost: 0,
    rate: 0,
    quantity: 0,
    unitOfMeasure: unitsOfMeasure[0],
    lineItemMemo: '',
    driverMemo: '',
    isActive: true,
    createdOn: new Date(),
    customerJobNumber: '',
    poNumber: '',
    isCustomPoNumber: false,
    isCustomerJobNumberCustom: false,
};

export function useDumpLineItemForm(props: IFormProps<DumpLineItemDto>, quote: QuoteDto | undefined) {
    const { save, cancel, initValues } = props;
    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });
    const [formQuote, setFormQuote] = useState<QuoteDto>();
    const [formEquipmentType, setFormEquipmentType] = useState<EquipmentTypeDto>();
    const [formDumpRate, setFormDumpRate] = useState<DumpRateDto>();
    const [formCost, setFormCost] = useState<number>();
    const [formRate, setFormRate] = useState<number>();
    const [formSite, setFormSite] = useState<SiteDto | undefined>(initValues?.site);
    const [formQuantity, setFormQuantity] = useState<number | undefined>();
    const [formUnitOfMeasure, setFormUnitOfMeasure] = useState<UnitOfMeasure>('Tons');
    const [formLineItemMemo, setFormLineItemMemo] = useState<string | undefined>(initValues?.lineItemMemo);
    const [formDriverMemo, setFormDriverMemo] = useState<string | undefined>(initValues?.driverMemo);
    const [formIsActive, setFormIsActive] = useState<boolean>(initValues?.isActive ?? true);
    const [formDumpType, setFormDumpType] = useState('');
    const [formAddressLine1, setFormAddressLine1] = useState(initValues?.haulToAddressLine1 ?? initValues?.site?.addressLine1 ?? '');
    const [formAddressLine2, setFormAddressLine2] = useState(initValues?.haulToAddressLine2 ?? initValues?.site?.addressLine2 ?? '');
    const [formCountry, setFormCountry] = useState(initValues?.haulToCountry ?? initValues?.site?.country ?? countries[2]);
    const [formDirections, setFormDirections] = useState(initValues?.haulToDirections ?? initValues?.site?.directions ?? '');
    const [formCity, setFormCity] = useState(initValues?.haulToCity ?? initValues?.site?.city ?? '');
    const [formState, setFormState] = useState(initValues?.haulToState ?? initValues?.site?.state ?? 'IN');
    const [formZipCode, setFormZipCode] = useState(initValues?.haulToZipCode ?? initValues?.site?.zipCode ?? '');
    const [formRegion, setFormRegion] = useState<RegionDto | undefined>(initValues?.haulToRegion ?? initValues?.site?.region);
    const [formCustomerJobNumber, setFormCustomerJobNumber] = useState<string | undefined>(
        initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? ''
    );
    const [formPoNumber, setFormPoNumber] = useState<string | undefined>(
        initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? ''
    );
    const [formQuotePoNumber, setFormQuotePoNumber] = useState<string | undefined>(quote?.poNumber);
    const [formQuoteCustomerJobNumber, setFormQuoteCustomerJobNumber] = useState<string | undefined>(quote?.customerJobNumber);
    const [formIsCustomPoNumber, setFormIsCustomPoNumber] = useState<boolean>(initValues?.isCustomPoNumber ?? false);
    const [formIsCustomerJobNumberCustom, setFormIsCustomerJobNumberCustom] = useState<boolean>(initValues?.isCustomerJobNumberCustom ?? false);

    /** Site name always matches the dump-rate site (AC: no override of site for haul-to address). */
    const formSiteName = useMemo(() => formSite?.name ?? '', [formSite?.name]);

    const isFobQuote = useMemo(() => {
        return formQuote?.type === QuoteType.FOB;
    }, [formQuote?.type]);

    useEffect(() => {
        setFormQuote(initValues?.quote);
        setFormEquipmentType(initValues?.equipmentType);
        setFormDumpRate(initValues?.dumpRate);
        setFormCost(initValues?.cost);
        setFormRate(initValues?.rate);
        setFormQuantity(initValues?.quantity);
        setFormUnitOfMeasure((initValues?.unitOfMeasure as UnitOfMeasure) ?? 'Tons');
        setFormDumpType(initValues?.dumpRate?.type ?? '');
        setFormLineItemMemo(initValues?.lineItemMemo);
        setFormDriverMemo(initValues?.driverMemo);
        setFormIsActive(initValues?.isActive ?? true);
        setFormSite(initValues?.site);
        setFormAddressLine1(initValues?.haulToAddressLine1 ?? initValues?.site?.addressLine1 ?? '');
        setFormAddressLine2(initValues?.haulToAddressLine2 ?? initValues?.site?.addressLine2 ?? '');
        setFormCountry(initValues?.haulToCountry ?? initValues?.site?.country ?? countries[2]);
        setFormDirections(initValues?.haulToDirections ?? initValues?.site?.directions ?? '');
        setFormCity(initValues?.haulToCity ?? initValues?.site?.city ?? '');
        setFormState(initValues?.haulToState ?? initValues?.site?.state ?? 'IN');
        setFormZipCode(initValues?.haulToZipCode ?? initValues?.site?.zipCode ?? '');
        setFormRegion(initValues?.haulToRegion ?? initValues?.site?.region);
        setFormQuotePoNumber(quote?.poNumber);
        setFormQuoteCustomerJobNumber(quote?.customerJobNumber);
        setFormPoNumber(initValues?.isCustomPoNumber ? (initValues?.poNumber === '' ? quote?.poNumber ?? '' : initValues?.poNumber ?? quote?.poNumber ?? '') : quote?.poNumber ?? '');
        setFormCustomerJobNumber(initValues?.isCustomerJobNumberCustom ? (initValues?.customerJobNumber === '' ? quote?.customerJobNumber ?? '' : initValues?.customerJobNumber ?? quote?.customerJobNumber ?? '') : quote?.customerJobNumber ?? '');
    }, [initValues, quote?.poNumber, quote?.customerJobNumber]);

    const [fieldErrors, setFieldErrors] = useState<Map<keyof IDumpLineItemFormValues, string>>(
        new Map([
            ['quoteId', ''],
            ['equipmentTypeId', ''],
            ['cost', ''],
            ['rate', ''],
            ['quantity', ''],
            ['dumpType', ''],
            ['unitOfMeasure', ''],
            ['lineItemMemo', ''],
            ['customerJobNumber', ''],
            ['poNumber', ''],
        ])
    );

    const formFieldValidators = useCallback(
        () =>
            new Map<keyof IDumpLineItemFormValues, IFormFieldValidationConfig>([
                [
                    'quoteId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Quote',
                    },
                ],
                [
                    'equipmentTypeId',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Equipment Type',
                    },
                ],
                [
                    'cost',
                    {
                        validators: [isNotBlank, isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Cost',
                    },
                ],
                [
                    'rate',
                    {
                        validators: [isNotBlank, isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Rate',
                    },
                ],
                [
                    'quantity',
                    {
                        validators: [isGreaterThanOrEqualTo(0)],
                        errorMessageEntityName: 'Quantity',
                    },
                ],
                [
                    'dumpType',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'Dump Type',
                    },
                ],
                [
                    'unitOfMeasure',
                    {
                        validators: [isNotBlank, isShorterThanMaxLength(20)],
                        errorMessageEntityName: 'Unit Of Measure',
                    },
                ],
                [
                    'poNumber',
                    {
                        validators: [isNotBlank],
                        errorMessageEntityName: 'PO Number',
                    },
                ],
            ]),
        []
    );

    const getCurrentFormValues = useCallback((): IDumpLineItemFormValues => {
        return {
            quoteId: formQuote?.id ?? emptyGuid,
            equipmentTypeId: formEquipmentType?.id ?? emptyGuid,
            dumpRateId: formDumpRate?.id ?? emptyGuid,
            rate: formRate,
            quantity: formQuantity ?? 1,
            unitOfMeasure: formUnitOfMeasure ?? '',
            cost: formCost,
            dumpType: formDumpType,
            lineItemMemo: formLineItemMemo,
            driverMemo: formDriverMemo,
            isActive: formIsActive,
            customerJobNumber: formCustomerJobNumber,
            poNumber: formPoNumber,
            isCustomPoNumber: formIsCustomPoNumber,
            isCustomerJobNumberCustom: formIsCustomerJobNumberCustom,
            haulToAddressLine1: formAddressLine1,
            haulToAddressLine2: formAddressLine2,
            haulToCity: formCity,
            haulToState: formState,
            haulToZipCode: formZipCode,
            haulToCountry: formCountry,
            haulToDirections: formDirections,
            haulToSiteName: formSite?.name ?? '',
            haulToRegion: formRegion,
        };
    }, [
        formCost,
        formCustomerJobNumber,
        formDumpRate?.id,
        formDumpType,
        formEquipmentType?.id,
        formIsActive,
        formLineItemMemo,
        formDriverMemo,
        formPoNumber,
        formQuantity,
        formQuote?.id,
        formRate,
        formUnitOfMeasure,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formSite?.name,
        formRegion,
    ]);

    const validateForm = useCallback(() => {
        const formValues = getCurrentFormValues();
        const validationResult = runFormValidation<Partial<IDumpLineItemFormValues>>(formValues, formFieldValidators());
        setFieldErrors(validationResult.errorMessages);
        return validationResult.isValid;
    }, [formFieldValidators, getCurrentFormValues]);

    const validateField = (fieldName: keyof IDumpLineItemFormValues) => {
        const validationConfig = formFieldValidators().get(fieldName);
        if (validationConfig) {
            const formValues = getCurrentFormValues();
            const fieldValue = formValues[fieldName];
            const { errorMessage } = runFieldValidation(fieldValue, validationConfig);

            if (errorMessage !== fieldErrors.get(fieldName)) {
                const updatedFieldErrors = _.cloneDeep(fieldErrors);
                updatedFieldErrors.set(fieldName, errorMessage);
                setFieldErrors(updatedFieldErrors);
            }
        }
    };

    const isFormDirty = () => {
        var isDirty = formIsActive !== initValues?.isActive;
        isDirty = isDirty || formQuote?.id !== initValues?.quote?.id;
        isDirty = isDirty || formEquipmentType?.id !== initValues?.equipmentType?.id;
        isDirty = isDirty || formRate !== initValues?.rate;
        isDirty = isDirty || formCost !== initValues?.cost;
        isDirty = isDirty || formQuantity !== initValues?.quantity;
        isDirty = isDirty || formUnitOfMeasure !== initValues?.unitOfMeasure;
        isDirty = isDirty || formDumpRate?.id !== initValues?.dumpRate?.id;
        isDirty = isDirty || formDumpType !== initValues?.dumpRate?.type;
        isDirty = isDirty || formLineItemMemo !== initValues?.lineItemMemo;
        isDirty = isDirty || formDriverMemo !== initValues?.driverMemo;
        isDirty = isDirty || formCustomerJobNumber !== initValues?.customerJobNumber;
        isDirty = isDirty || formPoNumber !== initValues?.poNumber;
        isDirty = isDirty || formIsCustomPoNumber !== initValues?.isCustomPoNumber;
        isDirty = isDirty || formIsCustomerJobNumberCustom !== initValues?.isCustomerJobNumberCustom;
        isDirty = isDirty || formAddressLine1 !== initValues?.haulToAddressLine1;
        isDirty = isDirty || formAddressLine2 !== initValues?.haulToAddressLine2;
        isDirty = isDirty || formCity !== initValues?.haulToCity;
        isDirty = isDirty || formState !== initValues?.haulToState;
        isDirty = isDirty || formZipCode !== initValues?.haulToZipCode;
        isDirty = isDirty || formCountry !== initValues?.haulToCountry;
        isDirty = isDirty || formDirections !== initValues?.haulToDirections;
        isDirty = isDirty || (initValues?.haulToSiteName ?? '') !== (formSite?.name ?? '');
        isDirty = isDirty || formRegion !== initValues?.haulToRegion;

        return isDirty;
    };

    const handleQuoteChange = (quote: QuoteDto) => {
        setFormQuote(quote);
    };

    const handleEquipmentTypeChange = (equipmentType?: EquipmentTypeDto) => {
        setFormEquipmentType(equipmentType);
    };

    const handleFormAddressChange = useCallback((address) => {
        setFormAddressLine1(address.addressLine1 ?? '');
        setFormAddressLine2(address.addressLine2 ?? '');
        setFormCity(address.city ?? '');
        setFormState(address.state ?? '');
        setFormZipCode(address.zipCode ?? '');
        setFormCountry(address.country ?? countries[2]);
        setFormDirections(address.directions ?? '');
    }, []);

    const handleFormRegionChange = useCallback((region: RegionDto | undefined) => {
        setFormRegion(region);
    }, []);

    const handleDumpRateChange = (dumpRate?: DumpRateDto) => {
        setFormDumpRate(dumpRate);
        setFormSite(dumpRate?.site);
        setFormCost(dumpRate?.cost);
        setFormRate(dumpRate?.rate);
        setFormUnitOfMeasure((dumpRate?.unitOfMeasure as UnitOfMeasure) ?? 'Tons');
        setFormAddressLine1(dumpRate?.site?.addressLine1 ?? '');
        setFormAddressLine2(dumpRate?.site?.addressLine2 ?? '');
        setFormCity(dumpRate?.site?.city ?? '');
        setFormState(dumpRate?.site?.state ?? 'IN');
        setFormZipCode(dumpRate?.site?.zipCode ?? '');
        setFormCountry(dumpRate?.site?.country ?? countries[2]);
        setFormDirections(dumpRate?.site?.directions ?? '');
        setFormRegion(dumpRate?.site?.region);
    };

    const handleRateChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormRate(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleLineItemMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormLineItemMemo(event.target.value ?? '');
    }, []);

    const handleDriverMemoChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormDriverMemo(event.target.value ?? '');
    }, []);

    const handleIsActiveChange = useCallback((_: ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
        setFormIsActive(isChecked);
    }, []);

    const handleDumpTypeChange = useCallback((event: SelectChangeEvent<string>) => {
        setFormDumpType(event.target.value);
        setFormDumpRate(undefined);
        setFormSite(undefined);
        setFormCost(undefined);
        setFormRate(undefined);
    }, []);

    const handleQuantityChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormQuantity(isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
    }, []);

    const handleCustomerJobNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormCustomerJobNumber(event.target.value ?? '');
    }, []);

    const handlePoNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setFormPoNumber(event.target.value ?? '');
    }, []);

    const handleSave = useCallback(() => {
        const isFormValid = validateForm();
        if (isFormValid) {
            const formValues = getCurrentFormValues();
            const updatedUserRecord = {
                ...(initValues ?? DEFAULT_DUMP_LINE_ITEM),
                ...formValues,
            };
            save(updatedUserRecord);
        }
    }, [getCurrentFormValues, initValues, save, validateForm]);

    const handleCancel = useCallback(() => {
        cancel();
    }, [cancel]);

    const handleIsCustomPoNumberChange = useCallback((value: boolean) => {
        setFormIsCustomPoNumber(value);
        if(!value) {
            setFormPoNumber(formQuotePoNumber);
        }
    }, [formQuotePoNumber, setFormPoNumber]);

    const handleIsCustomerJobNumberCustomChange = useCallback((value: boolean) => {
        setFormIsCustomerJobNumberCustom(value);
        if(!value) {
            setFormCustomerJobNumber(formQuoteCustomerJobNumber);
        }
    }, [formQuoteCustomerJobNumber, setFormCustomerJobNumber]);

    const formTotal = useMemo(() => {
        return (formQuantity ?? 0) * (formRate ?? 0);
    }, [formQuantity, formRate]);

    return {
        isFormDirty,
        handleSave,
        handleCancel,
        fieldErrors,
        handleQuoteChange,
        handleEquipmentTypeChange,
        handleRateChange,
        handleQuantityChange,
        handleIsActiveChange,
        handleDumpTypeChange,
        handleDumpRateChange,
        handleLineItemMemoChange,
        handleDriverMemoChange,
        handleCustomerJobNumberChange,
        handlePoNumberChange,
        handleIsCustomPoNumberChange,
        handleIsCustomerJobNumberCustomChange,
        handleFormAddressChange,
        handleFormRegionChange,
        formQuote,
        formEquipmentType,
        formDumpRate,
        formRate,
        formQuantity,
        formIsActive,
        formDumpType,
        formLineItemMemo,
        formDriverMemo,
        formCustomerJobNumber,
        formPoNumber,
        formIsCustomPoNumber,
        formIsCustomerJobNumberCustom,
        formQuotePoNumber,
        formQuoteCustomerJobNumber,
        formAddressLine1,
        formAddressLine2,
        formCity,
        formState,
        formZipCode,
        formCountry,
        formDirections,
        formSiteName,
        formRegion,
        regions,
        validateField,
        isFobQuote,
        initValues,
        formSite,
        formUnitOfMeasure,
        formCost,
        formTotal,
    };
}
